import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
	aiChatStatusVar,
	adminAiChatStatusVar,
	clearAiChatStatus,
	isChatDiscardedOnNavigation
} from "state/reactive-vars/ai-chat-status-var";
import { clearUploadingFiles } from "state/reactive-vars/uploading-files-var";
import { useChatWindowFiltersStore } from "state/zustand-stores/chat-window-filters-store";
import { AiChatOrigin, AiChatType } from "constants/field-values";
import routeNames from "constants/route-names";

// Drops the chats that don't survive navigation. Keyed on the pathname rather than on the route
// unmounting, because StrictMode fakes an unmount on mount and that would discard a live chat.
// Mount once, from a layout that outlives the routes below it.
export const useDiscardChatsOnNavigation = (allPropertyIds: string[]) => {
	const { pathname } = useLocation();
	const previousPathnameRef = useRef(pathname);
	const setPropertyFilters = useChatWindowFiltersStore(state => state.setPropertyFilters);

	// Read through a ref so a new array identity per render doesn't re-run the effect.
	const allPropertyIdsRef = useRef(allPropertyIds);
	allPropertyIdsRef.current = allPropertyIds;

	useEffect(() => {
		const previousPathname = previousPathnameRef.current;

		if (previousPathname === pathname) {
			return;
		}

		previousPathnameRef.current = pathname;

		// The AI Chat route is the only one that can hold a specialty chat.
		if (previousPathname === routeNames.AI_CHAT) {
			const { chatOrigin = AiChatOrigin.PRIMARY } = aiChatStatusVar();

			if (isChatDiscardedOnNavigation(AiChatType.CLIENT, chatOrigin)) {
				clearAiChatStatus(AiChatType.CLIENT);
				clearUploadingFiles(AiChatType.CLIENT);
				// Back to the filter's default (every property selected), which the specialty chat narrowed.
				// The chat window seeds the same value on mount, so the two can't race.
				setPropertyFilters(allPropertyIdsRef.current);
			}
		}

		if (previousPathname === routeNames.ADMIN_INTELLIGENCE) {
			const { chatOrigin = AiChatOrigin.PRIMARY } = adminAiChatStatusVar();

			if (isChatDiscardedOnNavigation(AiChatType.ADMIN, chatOrigin)) {
				clearAiChatStatus(AiChatType.ADMIN);
				clearUploadingFiles(AiChatType.ADMIN);
			}
		}
	}, [pathname, setPropertyFilters]);
};
